/**
 * Legal Contract Preview Console — side-by-side DOCX vs Legacy.
 *
 * Why this page?
 * ---------------
 * Mara Capelli (LoJack legal) needs to validate the DOCX rollout
 * WITHOUT manually downloading dozens of contracts. The whole panel
 * renders both engines in adjacent iframes plus a numeric diff badge
 * (pages, KB) so legal can spot layout regressions at a glance.
 *
 * Available to: super_admin, admin (legal sits there).
 * NOT visible to: it, viewer, dealer, seller.
 *
 * Auth: relies on the AuthContext axios interceptor to add the JWT
 * Bearer header. For the iframe PDF we use ``fetch`` (because <iframe
 * src> can't carry Authorization headers) → load as Blob → mint a
 * ``blob:`` URL → set as iframe src.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, RefreshCw, Download, AlertCircle, CheckCircle2, Info, AlertTriangle, Upload } from "lucide-react";
import { API } from "../config";

const ENGINES = [
  { id: "docx", label: "DOCX (Master template)", color: "emerald" },
  { id: "cms", label: "CMS Overlay (produzione attuale)", color: "slate" },
  // Fabrizio CR 07/08/2026 — third reference engine. Always available
  // (zero external deps). Used as fallback when both DOCX (LibreOffice
  // missing) and CMS (no template for this lang/country) can't render,
  // so Legal always sees SOMETHING useful — the PDF customers actually
  // signed in production via the legacy WeasyPrint pipeline.
  { id: "legacy", label: "WeasyPrint (riferimento legacy)", color: "amber" },
];

function fmtKB(bytes) {
  if (!Number.isFinite(bytes)) return "—";
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso.slice(0, 16);
  }
}

const LegalContractPreviewPage = () => {
  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [diagnose, setDiagnose] = useState(null);
  const [pdfUrls, setPdfUrls] = useState({ docx: "", cms: "", legacy: "" });
  const [loadingPdf, setLoadingPdf] = useState({ docx: false, cms: false, legacy: false });
  const [pdfErrors, setPdfErrors] = useState({ docx: "", cms: "", legacy: "" });

  // ── Initial list ──────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await axios.get(`${API}/admin/legal/contracts/recent?limit=50`);
      setContracts(r.data.items || []);
      setTotal(r.data.total || 0);
      if (r.data.items?.length && !selected) {
        setSelected(r.data.items[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento contratti");
    } finally {
      setLoadingList(false);
    }
  }, [selected]);

  useEffect(() => { fetchList(); }, []);

  // ── Per-contract PDF blobs + diagnose ────────────────────────
  const loadEngine = useCallback(async (engine, contractId) => {
    setLoadingPdf(s => ({ ...s, [engine]: true }));
    setPdfErrors(s => ({ ...s, [engine]: null }));
    const token = localStorage.getItem("lojack_token");
    try {
      const resp = await fetch(
        `${API}/admin/legal/contracts/${contractId}/preview?engine=${engine}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) {
        const txt = await resp.text();
        // Distinguish three failure modes so the UI shows the right
        // remediation to Mara/Legal (not a scary red "Errore di rendering"
        // for known-and-expected missing configuration):
        //   * ``docx_unavailable``  → LibreOffice missing (system-wide)
        //   * ``cms_no_template``   → No CMS template for this lang/country
        //                             (actionable via "Carica ora →" shortcut,
        //                             Fabrizio CR 07/08/2026)
        //   * ``error``             → real rendering crash → red alert
        let detail = txt;
        try {
          const parsed = JSON.parse(txt);
          detail = parsed?.detail || txt;
        } catch { /* not JSON — leave as text */ }
        const isDocxUnavailable =
          engine === "docx" && (resp.status === 503 || /soffice|libreoffice/i.test(detail));
        const isCmsNoTemplate =
          engine === "cms" && resp.status === 503 && /template CMS attivo/i.test(detail);
        let kind = "error";
        if (isDocxUnavailable) kind = "unavailable";
        else if (isCmsNoTemplate) kind = "cms_no_template";
        throw Object.assign(new Error(detail || `HTTP ${resp.status}`), {
          status: resp.status,
          kind,
        });
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrls(s => {
        // Revoke previous blob URL to avoid memory leaks
        if (s[engine]) URL.revokeObjectURL(s[engine]);
        return { ...s, [engine]: url };
      });
    } catch (e) {
      setPdfErrors(s => ({
        ...s,
        [engine]: {
          message: e.message || "Errore di rendering",
          kind: e.kind || "error",
          status: e.status || 0,
        },
      }));
    } finally {
      setLoadingPdf(s => ({ ...s, [engine]: false }));
    }
  }, []);

  const loadDiagnose = useCallback(async (contractId) => {
    setDiagnose(null);
    try {
      const r = await axios.get(`${API}/admin/legal/contracts/${contractId}/diagnose`);
      setDiagnose(r.data);
    } catch (_) { /* non-blocking */ }
  }, []);

  // Load PDFs + diagnose whenever the selected contract changes
  useEffect(() => {
    if (!selected?.id) return;
    // Cleanup previous blob URLs
    setPdfUrls(prev => {
      Object.values(prev).forEach(u => u && URL.revokeObjectURL(u));
      return { docx: "", cms: "", legacy: "" };
    });
    loadEngine("docx", selected.id);
    loadEngine("cms", selected.id);
    loadEngine("legacy", selected.id);
    loadDiagnose(selected.id);
  }, [selected?.id, loadEngine, loadDiagnose]);

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(pdfUrls).forEach(u => u && URL.revokeObjectURL(u));
    };
  }, []);

  const diffBadge = useMemo(() => {
    if (!diagnose?.engines) return null;
    const d = diagnose.engines.docx;
    const l = diagnose.engines.cms;
    if (!d?.ok || !l?.ok) return null;
    const pagesDiff = (d.pages ?? 0) - (l.pages ?? 0);
    const sizeDiff = (d.size_bytes ?? 0) - (l.size_bytes ?? 0);
    return { pagesDiff, sizeDiff };
  }, [diagnose]);

  // Fabrizio CR 07/08/2026 — when BOTH the DOCX and CMS engines fail
  // to render (typical for a preview cluster w/o LibreOffice AND w/o a
  // CMS template for the customer's country yet), show a yellow banner
  // at the top of the preview area explaining that the WeasyPrint
  // fallback panel is the SAME PDF customers actually signed. This
  // turns 3 red warnings into 1 actionable "here's your reference PDF"
  // message — Legal never sees an empty preview page anymore.
  const showLegacyFallbackBanner = useMemo(() => {
    if (!diagnose?.engines) return false;
    const { docx, cms, legacy } = diagnose.engines;
    return docx?.ok === false && cms?.ok === false && legacy?.ok === true;
  }, [diagnose]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="legal-preview-page">
      <div className="max-w-[1920px] mx-auto p-6">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-7 w-7 text-red-600" />
            <h1 className="text-3xl font-bold text-slate-900">
              Anteprima Contratti — Legal Side-by-Side
            </h1>
          </div>
          <p className="text-sm text-slate-600 max-w-3xl">
            Confronta il rendering del <strong>nuovo motore DOCX</strong> (basato sul master Word di Legal),
            del <strong>CMS Overlay</strong> — il PDF ufficiale attualmente firmato dai clienti in produzione
            (template caricato in <em>Doc. Legali</em> + overlay dati) — e del pipeline{" "}
            <strong>WeasyPrint legacy</strong> di riferimento (sempre disponibile come fallback). Strumento
            destinato al team Legal per validare il rollout della migrazione DOCX senza scaricare manualmente
            i PDF.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left rail: contract picker */}
          <aside className="col-span-12 lg:col-span-3 bg-white rounded-lg shadow-sm border border-slate-200 p-4 h-fit lg:sticky lg:top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-800">
                Contratti recenti <span className="text-slate-400 text-sm">({total})</span>
              </h2>
              <button
                onClick={fetchList}
                disabled={loadingList}
                className="p-1.5 hover:bg-slate-100 rounded transition"
                data-testid="legal-preview-refresh-list-btn"
                title="Ricarica lista"
              >
                <RefreshCw className={`h-4 w-4 text-slate-500 ${loadingList ? "animate-spin" : ""}`} />
              </button>
            </div>
            <ul className="space-y-1.5">
              {contracts.map((c) => {
                const active = selected?.id === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => setSelected(c)}
                      data-testid={`legal-preview-contract-${c.id}`}
                      className={`w-full text-left px-3 py-2 rounded-md transition text-sm
                        ${active
                          ? "bg-red-50 border-l-4 border-red-600 text-slate-900 font-medium"
                          : "hover:bg-slate-50 text-slate-700"
                        }`}
                    >
                      <div className="truncate font-medium">{c.customer_name || "(senza nome)"}</div>
                      <div className="text-xs text-slate-500 truncate">{c.customer_email}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {fmtDate(c.signed_at)} · {c.customer_country}
                      </div>
                    </button>
                  </li>
                );
              })}
              {!loadingList && contracts.length === 0 && (
                <li className="text-sm text-slate-400 text-center py-8">Nessun contratto firmato</li>
              )}
            </ul>
          </aside>

          {/* Right: side-by-side preview */}
          <main className="col-span-12 lg:col-span-9">
            {!selected ? (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center text-slate-400">
                Seleziona un contratto a sinistra per visualizzare l&apos;anteprima side-by-side.
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-xs uppercase text-slate-500 tracking-wide">Contratto selezionato</div>
                      <div className="font-semibold text-lg text-slate-900">{selected.customer_name}</div>
                      <div className="text-sm text-slate-500">
                        {selected.customer_email} · firmato {fmtDate(selected.signed_at)} · paese {selected.customer_country}
                      </div>
                    </div>
                    {diffBadge && (
                      <div className="flex items-center gap-2 text-xs">
                        <div className={`px-3 py-1.5 rounded-full border ${
                          diffBadge.pagesDiff === 0
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                        }`} data-testid="legal-preview-pages-diff-badge">
                          ΔPagine: {diffBadge.pagesDiff > 0 ? `+${diffBadge.pagesDiff}` : diffBadge.pagesDiff}
                        </div>
                        <div className={`px-3 py-1.5 rounded-full border ${
                          Math.abs(diffBadge.sizeDiff) < 100_000
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                        }`} data-testid="legal-preview-size-diff-badge">
                          ΔPeso: {diffBadge.sizeDiff > 0 ? `+${fmtKB(diffBadge.sizeDiff)}` : fmtKB(diffBadge.sizeDiff)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {showLegacyFallbackBanner && (
                  <div
                    className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4 flex items-start gap-3"
                    data-testid="legal-preview-legacy-fallback-banner"
                  >
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900 leading-relaxed">
                      <strong>Preview di riferimento — WeasyPrint legacy.</strong>{" "}
                      DOCX e CMS Overlay non sono disponibili in questo ambiente.
                      Il pannello <em>WeasyPrint (riferimento legacy)</em> qui sotto mostra il
                      PDF <strong>effettivamente firmato dal cliente</strong> in produzione, generato dal pipeline
                      di fallback. Utile per QA layout e traduzioni anche senza LibreOffice / template CMS.
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  {ENGINES.map(({ id, label, color }) => {
                    const engineInfo = diagnose?.engines?.[id];
                    const accent =
                      color === "emerald" ? "border-emerald-300 bg-emerald-50" :
                      color === "amber" ? "border-amber-300 bg-amber-50" :
                      "border-slate-300 bg-slate-50";
                    return (
                      <div key={id} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden flex flex-col" data-testid={`legal-preview-engine-${id}`}>
                        <div className={`px-4 py-3 border-b border-slate-200 flex items-center justify-between ${accent}`}>
                          <div>
                            <div className="font-semibold text-slate-800 flex items-center gap-2">
                              {engineInfo?.ok ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              ) : engineInfo?.ok === false ? (
                                <AlertCircle className="h-4 w-4 text-red-600" />
                              ) : null}
                              {label}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {engineInfo?.ok
                                ? `${engineInfo.pages ?? "?"} pagine · ${fmtKB(engineInfo.size_bytes)}`
                                : engineInfo?.error || "Caricamento…"}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => loadEngine(id, selected.id)}
                              disabled={loadingPdf[id]}
                              className="p-1.5 hover:bg-white/60 rounded transition"
                              data-testid={`legal-preview-reload-${id}-btn`}
                              title="Ricarica anteprima"
                            >
                              <RefreshCw className={`h-4 w-4 text-slate-600 ${loadingPdf[id] ? "animate-spin" : ""}`} />
                            </button>
                            {pdfUrls[id] && (
                              <a
                                href={pdfUrls[id]}
                                download={`${id}__${selected.customer_name || selected.id}.pdf`}
                                className="p-1.5 hover:bg-white/60 rounded transition"
                                data-testid={`legal-preview-download-${id}-btn`}
                                title="Scarica PDF"
                              >
                                <Download className="h-4 w-4 text-slate-600" />
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="relative bg-slate-100" style={{ height: "82vh" }}>
                          {loadingPdf[id] && !pdfUrls[id] && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
                            </div>
                          )}
                          {pdfErrors[id] && (
                            <div
                              className="absolute inset-0 flex items-center justify-center p-6 text-center"
                              data-testid={`legal-preview-error-${id}`}
                            >
                              {pdfErrors[id].kind === "unavailable" ? (
                                // Feature deliberately not available in this
                                // environment (e.g. LibreOffice not in the
                                // Emergent base image). Render as info-level
                                // — NOT a scary red error.
                                <div>
                                  <Info className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                                  <div
                                    className="text-sm text-blue-700 font-medium"
                                    data-testid={`legal-preview-unavailable-${id}`}
                                  >
                                    Motore in attesa di infra
                                  </div>
                                  <div className="text-xs text-slate-600 mt-2 max-w-sm leading-relaxed">
                                    Il motore DOCX richiede LibreOffice come dipendenza di sistema, non ancora presente nel container.
                                    Usa il motore <strong>CMS Overlay (produzione attuale)</strong> nella colonna a fianco — è il PDF ufficiale che i clienti firmano oggi.
                                  </div>
                                  <div className="text-[10px] text-slate-400 mt-3 italic max-w-sm">
                                    Per abilitarlo: ticket al team infra della piattaforma per aggiungere LibreOffice al base image.
                                  </div>
                                </div>
                              ) : pdfErrors[id].kind === "cms_no_template" ? (
                                // Fabrizio CR 07/08/2026 — CMS template missing
                                // for this customer's language/country. Actionable
                                // via the "Carica ora →" deep-link into the
                                // Doc. Legali uploader with lang/country
                                // pre-filled from ``diagnose.engines.cms.error_context``.
                                <div>
                                  <Info className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                                  <div
                                    className="text-sm text-amber-700 font-medium"
                                    data-testid={`legal-preview-cms-no-template-${id}`}
                                  >
                                    Template CMS non caricato
                                  </div>
                                  <div className="text-xs text-slate-600 mt-2 max-w-sm leading-relaxed">
                                    Nessun contratto attivo in <em>Doc. Legali</em>
                                    {(() => {
                                      const ctx = diagnose?.engines?.cms?.error_context;
                                      if (!ctx) return null;
                                      return (
                                        <>
                                          {" "}per lingua=<strong>{ctx.lang}</strong>,
                                          paese=<strong>{ctx.country || "?"}</strong>
                                          {ctx.product ? <>, prodotto=<strong>{ctx.product}</strong></> : null}
                                        </>
                                      );
                                    })()}.
                                    Fino a quando il template non è disponibile, il pipeline WeasyPrint qui a fianco continua a generare i PDF firmati in produzione.
                                  </div>
                                  {diagnose?.engines?.cms?.error_context?.country && (
                                    <a
                                      href={(() => {
                                        const ctx = diagnose.engines.cms.error_context;
                                        const params = new URLSearchParams({
                                          upload: "contract",
                                          lang: ctx.lang || "",
                                          country: ctx.country || "",
                                          ...(ctx.product ? { product: ctx.product } : {}),
                                        });
                                        return `/admin/legal?${params.toString()}`;
                                      })()}
                                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-lojack-red hover:bg-red-700 text-white rounded-lg text-sm font-medium transition"
                                      data-testid="legal-preview-cms-upload-shortcut"
                                    >
                                      <Upload className="h-4 w-4" />
                                      Carica template ora →
                                    </a>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                                  <div className="text-sm text-red-700 font-medium">Errore di rendering</div>
                                  <div className="text-xs text-slate-500 mt-1 max-w-sm">{pdfErrors[id].message}</div>
                                </div>
                              )}
                            </div>
                          )}
                          {pdfUrls[id] && (
                            <iframe
                              src={pdfUrls[id]}
                              title={`Preview ${label}`}
                              className="w-full h-full"
                              data-testid={`legal-preview-iframe-${id}`}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default LegalContractPreviewPage;
